/**
 * Smartsupp live chat.
 *
 * Loader zámerně nesedí v index.html — pouštíme ho až ve chvíli, kdy známe přihlášeného
 * uživatele, aby operátor hned viděl, s kým mluví. Identita se posílá dokumentovanými
 * metodami (`smartsupp('name', …)`); než dojede loader, sbírá je fronta ze snippetu,
 * takže je lze volat hned po injektáži.
 *
 * Na veřejných stránkách (login, magic linky) se tím chat vůbec nenačte.
 */

const SMARTSUPP_KEY = import.meta.env.VITE_SMARTSUPP_KEY || 'e5e0f8e76a62793a644ce0f3ca08ff829855d4e9';

/**
 * Vlastní data návštěvníka — operátor je vidí v panelu "Customer info". Klíč je zároveň
 * popisek, proto bez diakritiky a mezer (dashboard je zobrazuje tak, jak je pošleme).
 */
export type SmartsuppVariables = Record<string, string | number | boolean>;

export interface SmartsuppVisitor {
  name?: string;
  email?: string;
  phone?: string;
  /** Kód jazyka chatovacího okna, u nás 'cs' | 'en'. */
  language?: string;
  /** ID skupiny operátorů — kdyby se chaty měly routovat (klient vs. dodavatel). */
  group?: string;
  variables?: SmartsuppVariables;
}

type SmartsuppApi = (...args: unknown[]) => void;

type SmartsuppConfig = {
  key: string;
  hideWidget?: boolean;
};

type SmartsuppWindow = Window & {
  _smartsupp?: SmartsuppConfig;
  smartsupp?: SmartsuppApi;
};

const win = (): SmartsuppWindow => window as unknown as SmartsuppWindow;

/** Dokud se loader nestáhne, je `window.smartsupp` jen fronta — volat ho lze hned. */
export const getSmartsupp = (): SmartsuppApi | undefined => win().smartsupp;

const injectLoader = () => {
  const first = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.charset = 'utf-8';
  script.async = true;
  script.src = 'https://www.smartsuppchat.com/loader.js?';
  first.parentNode?.insertBefore(script, first);
};

/** Stub fronty z oficiálního snippetu — sbírá `smartsupp(...)` volání do doby, než dojede loader. */
const createQueue = () => {
  const queued: unknown[][] = [];
  const api = ((...args: unknown[]) => {
    queued.push(args);
  }) as SmartsuppApi & { _: unknown[][] };
  api._ = queued;
  win().smartsupp = api;
};

const applyVisitor = (visitor: SmartsuppVisitor) => {
  const api = getSmartsupp();
  if (!api) return;

  // Prázdný string maže dřívější hodnotu — jinak by po přelogování zůstala viset ta stará.
  api('name', visitor.name ?? '');
  api('email', visitor.email ?? '');
  api('phone', visitor.phone ?? '');
  api('variables', visitor.variables ?? {});
  if (visitor.language) api('language', visitor.language);
  if (visitor.group) api('group', visitor.group);
};

let booted = false;

/**
 * Nastartuje chat s identitou uživatele. Při opakovaném volání (změna uživatele nebo jazyka)
 * jen přepíše identitu, skript se už nenahrává znovu.
 */
export const bootSmartsupp = (visitor: SmartsuppVisitor) => {
  if (!booted) {
    booted = true;
    win()._smartsupp = {
      key: SMARTSUPP_KEY,
      // Bublina se nikde nezobrazuje sama, chat vyvolá až tlačítko na stránce podpory
      // (smartsupp('chat:open')), po odchodu ze stránky se zase schová.
      hideWidget: true,
    };
    createQueue();
    injectLoader();
  }

  applyVisitor(visitor);
};
